import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; 
import '../App.css';
import './Upload.css';

const Upload = () => {
  const [file, setFile] = useState(null);
  const [submissionName, setSubmissionName] = useState('');
  const [submissions, setSubmissions] = useState([]); // State for submission list
  const [errorMessage, setErrorMessage] = useState('');
  const [error, setError] = useState(null); // State for fetch errors
  const [isLoading, setIsLoading] = useState(false); // Loading state for both fetch and upload
  const [isDSAR, setIsDSAR] =  useState(true); // Default to checked
  const [isBankStatement, setIsBankStatement] = useState(false); // State for Bank Statement checkbox
  const navigate = useNavigate();

  // Helper function to check if the token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    const payload = JSON.parse(atob(token.split('.')[1])); // Decode the token payload
    const exp = payload.exp; // Get expiration time
    const currentTime = Math.floor(Date.now() / 1000); // Get current time in seconds
    return exp < currentTime; // Return true if token is expired
  };

  // Function to refresh the token if it has expired
  const refreshAuthToken = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return false; // No refresh token available, require login
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_FQDN}/api/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${refreshToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.access_token); // Save the new access token
        return true;
      } else {
        console.error('Failed to refresh token');
        return false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  };

  // Fetch the list of submissions from the backend
  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token || isTokenExpired(token)) {
          const refreshed = await refreshAuthToken(); // Try to refresh the token
          if (!refreshed) {
            navigate('/login'); // Redirect to login if token is expired and refresh failed
            return;
          }
        }

        const newToken = localStorage.getItem('token'); // Get the possibly refreshed token
        setIsLoading(true); // Start loading spinner
        const response = await fetch(`${process.env.REACT_APP_FQDN}/api/submissions`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${newToken}`, // Include the token in the Authorization header
            'Content-Type': 'application/json',
          },
        });
        
        const data = await response.json();
        
        if (response.ok) {
          setSubmissions(data.submissions || []); // Handle empty submissions list
        } else {
          setError(data.error || 'Error fetching submissions.');
        }
      } catch (error) {
        console.error('Error fetching submissions:', error);
        setError('Error fetching submissions.');
      } finally {
        setIsLoading(false); // Stop loading spinner
      }
    };

    fetchSubmissions();
  }, [navigate]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!submissionName) {
      alert('Please provide a submission name.');
      return;
    }

    // Check if file is provided
    if (!file) {
      alert('Please select a file.');
      return;
    }

    // Check if at least one checkbox is selected
    if (!isDSAR && !isBankStatement) {
      alert('Please select either DSAR or Bank Statement.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('submissionName', submissionName);
    formData.append('isDSAR', isDSAR); // Add DSAR checkbox value
    formData.append('isBankStatement', isBankStatement); // Add Bank Statement checkbox value

    try {
      const token = localStorage.getItem('token');
      if (!token || isTokenExpired(token)) {
        const refreshed = await refreshAuthToken(); // Try to refresh the token
        if (!refreshed) {
          navigate('/login'); // Redirect to login if token is expired and refresh failed
          return;
        }
      }

      const newToken = localStorage.getItem('token'); // Get the possibly refreshed token
      setIsLoading(true); // Start loading spinner during upload
      const response = await fetch(`${process.env.REACT_APP_FQDN}/api/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${newToken}`, // Include the token in the Authorization header
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        // Redirect to /pdfs on success
        alert(data.message);
        navigate('/pdfs');
      } else {
        // Handle error messages (e.g., duplicate uploadName)
        setErrorMessage(data.error || 'Upload failed.');
        alert(errorMessage);
        console.error('Upload failed:', errorMessage);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setErrorMessage('Error uploading file.');
      alert(errorMessage);
    } finally {
      setIsLoading(false); // Stop loading spinner after upload
    }
  };

  // Ensure only one checkbox is selected at a time
  const handleCheckboxChange = (checkbox) => {
    if (checkbox === 'DSAR') {
      setIsDSAR(true);
      setIsBankStatement(false);
    } else if (checkbox === 'BankStatement') {
      setIsDSAR(false);
      setIsBankStatement(true);
    }
  };

  return (
    <div>
      <h2>Upload a PDF or ZIP File</h2>
      <p>Please enter a <strong>Submission Name</strong>. The <strong>Submission Name</strong> will group multiple files together.</p>
      
      {/* Display the list of submissions */}
      <div className='upload'>
        <h3>Existing Submissions</h3>
        {error ? (
          <p style={{ color: 'red' }}>{error}</p>
        ) : (
          <>
            {submissions.length > 0 ? (
              <select className='upload-page'
                value={submissionName} 
                onChange={(e) => setSubmissionName(e.target.value)}
              >
                <option value="">Select a Submission</option>
                {submissions.map((submission) => (
                  <option key={submission.id} value={submission.submission_name}>
                    {submission.submission_name}
                  </option>
                ))}
              </select>
            ) : (
              <p>No existing submissions available.</p>
            )}
          </>
        )}
      </div>

      <p>OR</p>

      {/* Input to create a new submission */}
      <input 
        type="text" 
        className='upload-page'
        value={submissionName} 
        onChange={(e) => setSubmissionName(e.target.value)} 
        placeholder="New Submission Name" 
        disabled={isLoading} // Disable input while loading
      />
      
      {/* File input */}
      <input 
        type="file" 
        className='upload-page'
        onChange={handleFileChange} 
        accept=".pdf, .zip" 
        disabled={isLoading} // Disable file input while loading
      />

      {/* Checkboxes for DSAR and Bank Statement */}
      <div className="checkbox-group">
        <p>What are you processing?</p>
        <label className="checkbox-option">
          <input 
            type="checkbox" 
            checked={isDSAR} 
            onChange={() => handleCheckboxChange('DSAR')}
            disabled={isLoading}
          />
          DSAR
        </label>
        {/*<p>OR</p>
        <label className="checkbox-option">
          <input 
            type="checkbox" 
            checked={isBankStatement} 
            onChange={() => handleCheckboxChange('BankStatement')}
            disabled={isLoading}
          />
          Bank Statement
        </label>*/}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button onClick={handleUpload} disabled={isLoading} style={{ width: "300px"}} > {/* Disable upload button while loading */}
          Submit & Start Processing
        </button>
      </div>

      {isLoading && <div className="login-spinner"></div>} {/* Show spinner while uploading */}
    </div>
  );
};

export default Upload;
